import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  BarChart3,
  LayoutDashboard,
  Users,
  Receipt,
  FileText,
  CalendarDays,
  ListChecks,
  LogOut,
  Menu,
  Presentation,
  Settings,
  Sun,
} from "lucide-react";
import { Logo, LogoMark } from "@/components/Logo";
import { api } from "@/lib/api";
import { useToast } from "@/components/ui/Toast";
import { cn } from "@/lib/utils";
import { todayBadgeCount } from "@/lib/nav-badge";
import { TODAY_CHANGED_EVENT } from "@/lib/pointage";
import { PeriodSelector } from "@/components/layout/PeriodSelector";

type NavItem = {
  to: string;
  icon: typeof LayoutDashboard;
  label: string;
  end?: boolean;
};

const NAV: NavItem[] = [
  { to: "/aujourdhui", icon: Sun, label: "Aujourd'hui" },
  { to: "/", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/statistiques", icon: BarChart3, label: "Statistiques" },
  { to: "/prospects", icon: Users, label: "Prospects" },
  { to: "/todo", icon: ListChecks, label: "To-Do" },
  { to: "/webinaires", icon: Presentation, label: "Webinaires" },
  { to: "/ventes", icon: Receipt, label: "Ventes" },
  { to: "/prestations", icon: FileText, label: "Prestations" },
  { to: "/calendrier", icon: CalendarDays, label: "Calendrier" },
];

// Mobile (< lg) : barre d'onglets en bas + feuille « Plus ».
type TabId = "today" | "prospects" | "todo" | "agenda" | "more";

const TAB_ROUTES: { id: TabId; to: string }[] = [
  { id: "today", to: "/aujourdhui" },
  { id: "prospects", to: "/prospects" },
  { id: "todo", to: "/todo" },
  { id: "agenda", to: "/calendrier" },
];

const TABS: { id: TabId; label: string; icon: typeof Sun; to?: string }[] = [
  { id: "today", label: "Aujourd'hui", icon: Sun, to: "/aujourdhui" },
  { id: "prospects", label: "Prospects", icon: Users, to: "/prospects" },
  { id: "todo", label: "To-Do", icon: ListChecks, to: "/todo" },
  { id: "agenda", label: "Agenda", icon: CalendarDays, to: "/calendrier" },
  { id: "more", label: "Plus", icon: Menu },
];

const MORE_NAV: NavItem[] = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/statistiques", icon: BarChart3, label: "Statistiques" },
  { to: "/webinaires", icon: Presentation, label: "Webinaires" },
  { to: "/ventes", icon: Receipt, label: "Ventes" },
  { to: "/prestations", icon: FileText, label: "Prestations" },
  { to: "/parametres", icon: Settings, label: "Paramètres" },
];

// `/prospects` et `/prospects/141` appartiennent à la même section.
function inSection(path: string, to: string): boolean {
  return path === to || path.startsWith(`${to}/`);
}

const ORDER_KEY = "pccrm.nav.order";

// Load the saved order, reconciled with the current NAV definitions so that
// added/removed items don't break a stale saved order.
function loadOrder(): string[] {
  const known = NAV.map((n) => n.to);
  try {
    const saved = JSON.parse(localStorage.getItem(ORDER_KEY) ?? "[]");
    if (Array.isArray(saved)) {
      const valid = saved.filter(
        (t): t is string => typeof t === "string" && known.includes(t),
      );
      // Entrées nouvelles : « Aujourd'hui » passe en tête (une seule fois : l'ordre est ensuite
      // sauvegardé, un glisser-déposer ultérieur l'emporte) ; les autres reprennent leur place
      // d'origine (avant la première entrée connue qui les suit dans NAV) plutôt que la fin.
      const out = [...valid];
      if (!out.includes("/aujourdhui")) out.unshift("/aujourdhui");
      known.forEach((t, i) => {
        if (out.includes(t)) return;
        const next = known.slice(i + 1).find((k) => out.includes(k));
        out.splice(next ? out.indexOf(next) : out.length, 0, t);
      });
      return out;
    }
  } catch {
    /* ignore malformed value */
  }
  return known;
}

export function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  // Le sélecteur de période n'apparaît que sur les pages filtrables.
  const PERIOD_ROUTES = ["/", "/prospects", "/ventes", "/prestations"];
  const showPeriod = PERIOD_ROUTES.includes(location.pathname);
  const toast = useToast();
  const [moreOpen, setMoreOpen] = useState(false);
  const [badge, setBadge] = useState(0);
  const [order, setOrder] = useState<string[]>(loadOrder);
  const [draggingTo, setDraggingTo] = useState<string | null>(null);
  const dragIndex = useRef<number | null>(null);

  // La feuille « Plus » se ferme à chaque navigation et sur Échap.
  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!moreOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMoreOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [moreOpen]);

  // Pastille « Aujourd'hui » : rechargée à chaque changement de route et après un pointage ; un échec la masque.
  useEffect(() => {
    let cancelled = false;
    const refresh = () => {
      api
        .today()
        .then((d) => !cancelled && setBadge(todayBadgeCount(d)))
        .catch(() => !cancelled && setBadge(0));
    };
    refresh();
    window.addEventListener(TODAY_CHANGED_EVENT, refresh);
    return () => {
      cancelled = true;
      window.removeEventListener(TODAY_CHANGED_EVENT, refresh);
    };
  }, [location.pathname]);

  const path = location.pathname;
  const routeTab: TabId =
    TAB_ROUTES.find((t) => inSection(path, t.to))?.id ?? "more";
  const activeTab: TabId = moreOpen ? "more" : routeTab;

  useEffect(() => {
    localStorage.setItem(ORDER_KEY, JSON.stringify(order));
  }, [order]);

  const orderedNav = order
    .map((to) => NAV.find((n) => n.to === to))
    .filter((n): n is NavItem => Boolean(n));

  function onDragStart(e: React.DragEvent, index: number, to: string) {
    dragIndex.current = index;
    setDraggingTo(to);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", to);
  }

  function onDragOver(e: React.DragEvent, overIndex: number) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const from = dragIndex.current;
    if (from === null || from === overIndex) return;
    dragIndex.current = overIndex;
    setOrder((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(overIndex, 0, moved);
      return next;
    });
  }

  function onDragEnd() {
    dragIndex.current = null;
    setDraggingTo(null);
  }

  async function logout() {
    try {
      await api.logout();
      navigate("/login");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erreur");
    }
  }

  return (
    <div className="min-h-dvh flex">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex flex-col w-64 bg-[var(--color-navy-700)] text-white p-4 sticky top-0 h-dvh">
        <div className="flex items-center gap-3 px-2 py-3">
          <LogoMark size={36} />
          <div className="leading-tight">
            <div className="font-bold text-white tracking-tight">
              Power Coaching
            </div>
            <div className="text-xs text-[var(--color-navy-200)]">
              CRM Closer
            </div>
          </div>
        </div>
        <nav className="mt-6 flex flex-col gap-1">
          {orderedNav.map((item, i) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              draggable
              onDragStart={(e) => onDragStart(e, i, item.to)}
              onDragOver={(e) => onDragOver(e, i)}
              onDragEnd={onDragEnd}
              className={({ isActive }) =>
                cn(
                  "nav-link cursor-grab active:cursor-grabbing",
                  isActive && "active",
                  draggingTo === item.to && "opacity-40",
                )
              }
              title="Glisser pour réordonner"
            >
              <item.icon size={18} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto pt-4 border-t border-white/10 space-y-1">
          <NavLink
            to="/parametres"
            className={({ isActive }) =>
              cn("nav-link", isActive && "active")
            }
          >
            <Settings size={18} />
            <span>Paramètres</span>
          </NavLink>
          <button
            type="button"
            onClick={logout}
            className="nav-link w-full text-left"
          >
            <LogOut size={18} />
            <span>Se déconnecter</span>
          </button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-30 bg-[var(--color-navy-700)] text-white px-4 safe-top">
        <div className="h-14 flex items-center">
          <Logo variant="white" height={26} />
        </div>
      </div>

      {/* Mobile : feuille « Plus » (au-dessus de la barre z-30, sous les modales z-50) */}
      {moreOpen && (
        <div className="lg:hidden">
          <button
            type="button"
            aria-label="Fermer le menu"
            onClick={() => setMoreOpen(false)}
            className="fixed inset-x-0 top-0 bottom-[calc(3.25rem+1px+env(safe-area-inset-bottom))] z-40 bg-black/40"
          />
          <div
            role="dialog"
            aria-label="Plus"
            className="sheet-in fixed inset-x-0 bottom-[calc(3.25rem+1px+env(safe-area-inset-bottom))] z-40 rounded-t-2xl bg-white p-2 shadow-[0_-8px_24px_rgb(0_0_0/0.12)]"
          >
            <nav className="flex flex-col">
              {MORE_NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 min-h-12 px-3 rounded-lg text-[15px] font-medium text-[var(--color-foreground)]",
                      isActive && "bg-[var(--color-navy-50)] text-[var(--color-navy-600)]",
                    )
                  }
                >
                  <item.icon size={20} />
                  <span>{item.label}</span>
                </NavLink>
              ))}
              <button
                type="button"
                onClick={logout}
                className="flex items-center gap-3 min-h-12 px-3 rounded-lg text-[15px] font-medium text-[var(--color-muted-foreground)] text-left"
              >
                <LogOut size={20} />
                <span>Se déconnecter</span>
              </button>
            </nav>
          </div>
        </div>
      )}

      {/* Mobile : barre d'onglets */}
      <nav
        aria-label="Navigation principale"
        className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-[var(--color-border)] safe-bottom"
      >
        <div className="grid grid-cols-5">
          {TABS.map((tab) => {
            const active = activeTab === tab.id;
            const content = (
              <>
                <span className="relative">
                  <tab.icon size={22} />
                  {tab.id === "today" && badge > 0 && (
                    <span
                      aria-label={`${badge} à traiter`}
                      className="absolute -top-1.5 -right-2.5 min-w-4 h-4 px-1 rounded-full bg-[var(--color-coral-500)] text-white text-[10px] font-semibold leading-4 text-center"
                    >
                      {badge}
                    </span>
                  )}
                </span>
                <span>{tab.label}</span>
              </>
            );
            const cls = cn(
              "flex flex-col items-center justify-center gap-0.5 min-h-[3.25rem] text-[11px] font-medium",
              active
                ? "text-[var(--color-navy-600)]"
                : "text-[var(--color-muted-foreground)]",
            );
            return tab.to ? (
              <NavLink
                key={tab.id}
                to={tab.to}
                aria-current={active ? "page" : undefined}
                onClick={() => setMoreOpen(false)}
                className={cls}
              >
                {content}
              </NavLink>
            ) : (
              <button
                key={tab.id}
                type="button"
                aria-expanded={moreOpen}
                onClick={() => setMoreOpen((v) => !v)}
                className={cls}
              >
                {content}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Main */}
      <main className="flex-1 min-w-0 pt-[calc(3.5rem+env(safe-area-inset-top))] lg:pt-0 max-lg:pb-[calc(4.5rem+env(safe-area-inset-bottom))]">
        <div className="container-app py-6 lg:py-8">
          {showPeriod && <PeriodSelector />}
          <Outlet />
        </div>
      </main>
    </div>
  );
}
